import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { fmtHours } from "@/lib/utils"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface TimeEntry {
  id: number
  project_id: number
  description: string
  entry_date: string
  hours: number | null
  started_at: string | null
  stopped_at: string | null
  billable: boolean
}

interface ProjectOption {
  id: number
  name: string
  customer_name: string
}

interface Props {
  [key: string]: unknown
  time_entry: TimeEntry
  projects: ProjectOption[]
}

function isoToLocalTime(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

function computeHours(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0
  const [sh, sm] = startTime.split(":").map(Number)
  const [eh, em] = endTime.split(":").map(Number)
  return Math.max((eh * 60 + em - (sh * 60 + sm)) / 60, 0)
}

export default function TimeTrackingEdit() {
  const { time_entry, projects } = usePage<Props>().props

  const initialStart = isoToLocalTime(time_entry.started_at) || "09:00"
  const initialEnd =
    isoToLocalTime(time_entry.stopped_at) ||
    (time_entry.hours
      ? (() => {
          const [sh, sm] = initialStart.split(":").map(Number)
          const total = sh * 60 + sm + time_entry.hours * 60
          const eh = Math.floor(total / 60) % 24
          const em = Math.round(total % 60)
          return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`
        })()
      : "10:00")

  const { data, setData, patch, processing, errors, transform } = useForm({
    project_id: time_entry.project_id ? String(time_entry.project_id) : "none",
    description: time_entry.description,
    entry_date: time_entry.entry_date,
    start_time: initialStart,
    end_time: initialEnd,
    billable: time_entry.billable,
  })

  transform((d) => {
    const hours = computeHours(d.start_time, d.end_time)
    const startedAt = new Date(`${d.entry_date}T${d.start_time}:00`).toISOString()
    const stoppedAt = new Date(`${d.entry_date}T${d.end_time}:00`).toISOString()
    return {
      time_entry: {
        project_id: d.project_id === "none" ? null : d.project_id,
        description: d.description,
        entry_date: d.entry_date,
        hours: hours.toFixed(2),
        started_at: startedAt,
        stopped_at: stoppedAt,
        billable: d.billable,
      },
      view: "list",
    }
  })

  const allErrors = Object.values(errors)
  const computedHours = computeHours(data.start_time, data.end_time)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/time_entries/${time_entry.id}`)
  }

  return (
    <>
      <Head title="Edit Time Entry - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/time_entries">Time Tracking</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit Entry</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/time_entries">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Time Entry</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={data.project_id} onValueChange={(v) => setData("project_id", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.customer_name} — {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="entry_date">Date</Label>
                    <Input
                      id="entry_date"
                      type="date"
                      value={data.entry_date}
                      onChange={(e) => setData("entry_date", e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="start_time">Start</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={data.start_time}
                        onChange={(e) => setData("start_time", e.target.value)}
                        required
                      />
                    </div>
                    <span className="text-muted-foreground pb-2">to</span>
                    <div className="space-y-2">
                      <Label htmlFor="end_time">End</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={data.end_time}
                        onChange={(e) => setData("end_time", e.target.value)}
                        required
                      />
                    </div>
                    <span className="text-muted-foreground pb-2 text-sm">{fmtHours(computedHours)}</span>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={data.billable}
                        onChange={(e) => setData("billable", e.target.checked)}
                        className="rounded"
                      />
                      Billable
                    </label>
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href="/time_entries">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing || computedHours <= 0}>
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

TimeTrackingEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
